import { Component } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

/**
 * Catches render-time errors in its subtree so a single broken screen never blanks the whole
 * app. Offers a soft reset (re-render) and a hard reload.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack alignItems="center" spacing={2} textAlign="center" sx={{ maxWidth: 440 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: 'error.main',
              bgcolor: (t) => `${t.palette.error.main}14`,
            }}
          >
            <ReportProblemRoundedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            This section hit an unexpected error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {this.state.error?.message || 'An unexpected error occurred while rendering this view.'}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={this.handleReset} startIcon={<RefreshRoundedIcon />}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
